import React, { WeakValidationMap } from 'react';
import PropTypes from 'prop-types';
import ForgePropTypes from '../utils/propTypes';
import { forgeClassHelper } from '../utils/classes';
import { AlertTypes } from '../utils/constants';
import SuccessLarge from '@athena/forge-icons/dist/SuccessLarge';
import AttentionLarge from '@athena/forge-icons/dist/AttentionLarge';
import InfoLarge from '@athena/forge-icons/dist/InfoLarge';
import CriticalLarge from '@athena/forge-icons/dist/CriticalLarge';
import CloseSmall from '@athena/forge-icons/dist/CloseSmall';
import Button from '../Button';

const classes = forgeClassHelper({ name: 'banner' });

const iconClasses = { ...classes({ element: 'icon' }) };

const iconLookup = {
  success: <SuccessLarge {...iconClasses} />,
  attention: <AttentionLarge {...iconClasses} />,
  info: <InfoLarge {...iconClasses} />,
  critical: <CriticalLarge {...iconClasses} />,
};

export interface BannerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Gives a default left border and header with icon that corresponds to status level */
  alertType?: AlertTypes;
  /** Adds a class to the root element of the component */
  className?: string;
  /** Callback called when close button is clicked, if not included no close is shown */
  onHide?: React.MouseEventHandler<HTMLButtonElement>;
  /** BannerItems to render inside the Banner */
  children: React.ReactNode;
}

const Banner = ({ alertType = 'info', onHide, className, children, ...props }: BannerProps): React.ReactElement => (
  <div
    {...props}
    {...classes({
      extra: className,
      modifiers: [alertType],
    })}
    role="note"
  >
    {alertType && iconLookup[alertType]}
    <div {...classes({ element: 'items' })}>{children}</div>
    {onHide && (
      <Button
        {...classes({ element: 'dismiss-button' })}
        icon={CloseSmall}
        size="tiny"
        variant="tertiary"
        onClick={onHide}
      />
    )}
  </div>
);

const bannerPropTypes: WeakValidationMap<BannerProps> = {
  /** Gives a default left border and header with icon that corresponds to status level */
  alertType: ForgePropTypes.alertTypes,
  /** BannerItems to render inside the Banner */
  children: PropTypes.node.isRequired,
  /** Callback called when close button is clicked, if not included no close is shown */
  onHide: PropTypes.func,
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
};
Banner.propTypes = bannerPropTypes;

export default Banner;
